import dayjs, { type Dayjs } from 'dayjs';
import { AddDialog } from '@/components/AddDialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  editDialogInputBackgroundColor,
  EntityEditDialog,
} from '@/features/shared/components/entityDetails/EntityEditDialog';
import {
  EntityEditDateField,
  EntityEditSelectField,
  EntityEditStatusField,
  EntityEditTagsField,
} from '@/features/shared/components/entityDetails/EntityEditFields';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getFilterLabel } from '@/shared/copy/filters';
import { getFormLabel } from '@/shared/copy/forms';
import { LinkSelectDialog, UnlinkItemsDialog } from '@/ui/composites/entityLinking';
import { SingleSelectFilterField } from '@/ui/composites/filters/SingleSelectFilterField';
import { optionalLabel } from '@/ui/hooks/validation';
import { DatePicker } from '@/ui/primitives';
import { TextField } from '@/ui/primitives/TextField';
import type { FieldErrorProps } from '@/ui/hooks/useFormErrors';
import type {
  FeatureDto,
  ReleaseListItemDto,
  SuiteListItemDto,
  TestListItemDto,
} from '@flowqa/shared';
import type { ReactElement } from 'react';

type StatusOption = { value: FeatureDto['status']; label: string };
type ProjectOption = { id: string; name: string };

type FeatureFormValues = {
  name: string;
  status: FeatureDto['status'];
  projectId: string;
  releaseId: string;
  plannedFor: string;
  releasedAt: string;
  tags?: string[];
};

type FeatureFormHandlers = {
  onNameChange: (value: string) => void;
  onStatusChange: (value: FeatureDto['status']) => void;
  onProjectChange?: (value: string) => void;
  onReleaseChange?: (value: string) => void;
  onPlannedForChange: (value: string) => void;
  onReleasedAtChange: (value: string) => void;
  onTagsChange?: (value: string[]) => void;
};

type AddFeatureDialogProps = {
  open: boolean;
  title: string;
  saving?: boolean;
  values: FeatureFormValues;
  errors?: { name?: FieldErrorProps };
  statusOptions: StatusOption[];
  projectOptions: ProjectOption[];
  releaseOptions: ReleaseListItemDto[];
  showProjectSelect: boolean;
  releaseDisabled: boolean;
  contextText?: string;
  errorMessage?: string | null;
  onClose: () => void;
  onSave: () => void;
  handlers: FeatureFormHandlers;
};

export const AddFeatureDialog = ({
  open,
  title,
  saving = false,
  values,
  errors,
  statusOptions,
  projectOptions,
  releaseOptions,
  showProjectSelect,
  releaseDisabled,
  contextText,
  errorMessage,
  onClose,
  onSave,
  handlers,
}: AddFeatureDialogProps): ReactElement => {
  const releaseLabel = optionalLabel('Release');
  const plannedForLabel = optionalLabel('Planned For');
  const releasedAtLabel = optionalLabel('Released At');
  const Field = TextField;
  const toDayjs = (value: string): Dayjs | null => (value ? dayjs(value) : null);
  const toDateString = (value: Dayjs | null): string => (value ? value.format('YYYY-MM-DD') : '');

  return (
    <AddDialog
      open={open}
      title={title}
      saving={saving}
      description={contextText}
      errorMessage={errorMessage}
      onClose={onClose}
      onSave={onSave}
    >
      <Field
        label={getFormLabel('name')}
        value={values.name}
        onChange={(event) => {
          handlers.onNameChange(event.target.value);
        }}
        fieldStyle="settings"
        required
        autoFocus
        {...(errors?.name ?? {})}
      />
      <SingleSelectFilterField
        required
        label={getFormLabel('status')}
        value={values.status}
        fullWidth
        options={statusOptions.map((status) => ({
          value: status.value,
          label: status.label,
        }))}
        onChange={(nextValue) => {
          handlers.onStatusChange(nextValue as FeatureDto['status']);
        }}
      />
      {showProjectSelect ? (
        <SingleSelectFilterField
          required
          label={getFilterLabel('project')}
          value={values.projectId}
          fullWidth
          options={projectOptions.map((project) => ({
            value: project.id,
            label: project.name,
          }))}
          onChange={(nextValue) => {
            handlers.onProjectChange?.(nextValue);
          }}
        />
      ) : null}
      {releaseOptions.length > 0 ? (
        <SingleSelectFilterField
          label={releaseLabel}
          value={values.releaseId}
          fullWidth
          disabled={releaseDisabled}
          options={[
            { value: '', label: 'No Release' },
            ...releaseOptions.map((release) => ({ value: release.id, label: release.name })),
          ]}
          onChange={(nextValue) => {
            handlers.onReleaseChange?.(nextValue);
          }}
        />
      ) : null}
      <DatePicker
        label={plannedForLabel}
        value={toDayjs(values.plannedFor)}
        onChange={(nextValue) => {
          handlers.onPlannedForChange(toDateString(nextValue));
        }}
      />
      <DatePicker
        label={releasedAtLabel}
        value={toDayjs(values.releasedAt)}
        onChange={(nextValue) => {
          handlers.onReleasedAtChange(toDateString(nextValue));
        }}
      />
    </AddDialog>
  );
};

type EditFeatureDialogProps = {
  open: boolean;
  saving?: boolean;
  values: FeatureFormValues;
  errors?: { name?: FieldErrorProps };
  statusOptions: StatusOption[];
  releaseOptions: ReleaseListItemDto[];
  errorMessage?: string | null;
  onClose: () => void;
  onSave: () => void;
  handlers: FeatureFormHandlers;
};

export const EditFeatureDialog = ({
  open,
  saving = false,
  values,
  errors,
  statusOptions,
  releaseOptions,
  errorMessage,
  onClose,
  onSave,
  handlers,
}: EditFeatureDialogProps): ReactElement => {
  const releaseLabel = optionalLabel('Release');
  const plannedForLabel = optionalLabel('Planned For');
  const releasedAtLabel = optionalLabel('Released At');
  const Field = TextField;
  const toDayjs = (value: string): Dayjs | null => (value ? dayjs(value) : null);
  const toDateString = (value: Dayjs | null): string => (value ? value.format('YYYY-MM-DD') : '');

  return (
    <EntityEditDialog
      open={open}
      entityLabel="Feature"
      entityName={values.name}
      saving={saving}
      errorMessage={errorMessage}
      onClose={onClose}
      onSave={onSave}
    >
      <Field
        label={getFormLabel('name')}
        value={values.name}
        onChange={(event) => {
          handlers.onNameChange(event.target.value);
        }}
        fieldStyle="settings"
        inputBackgroundColor={editDialogInputBackgroundColor}
        required
        autoFocus
        {...(errors?.name ?? {})}
      />
      <EntityEditSelectField
        label={releaseLabel}
        value={values.releaseId}
        options={[
          { value: '', label: 'No Release' },
          ...releaseOptions.map((release) => ({ value: release.id, label: release.name })),
        ]}
        onChange={(nextValue) => {
          handlers.onReleaseChange?.(nextValue);
        }}
      />
      <EntityEditStatusField
        value={values.status}
        options={statusOptions}
        onChange={(nextValue) => {
          handlers.onStatusChange(nextValue);
        }}
      />
      <EntityEditDateField
        label={plannedForLabel}
        value={toDayjs(values.plannedFor)}
        onChange={(nextValue) => {
          handlers.onPlannedForChange(toDateString(nextValue));
        }}
      />
      <EntityEditDateField
        label={releasedAtLabel}
        value={toDayjs(values.releasedAt)}
        onChange={(nextValue) => {
          handlers.onReleasedAtChange(toDateString(nextValue));
        }}
      />
      <EntityEditTagsField
        open={open}
        projectId={values.projectId}
        tags={values.tags ?? []}
        helperText="Tags will be applied only to this feature."
        onTagsChange={(nextTags) => {
          handlers.onTagsChange?.(nextTags);
        }}
      />
    </EntityEditDialog>
  );
};

type DeleteFeatureDialogProps = {
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

export const DeleteFeatureDialog = ({
  open,
  loading = false,
  onClose,
  onConfirm,
}: DeleteFeatureDialogProps): ReactElement => {
  return (
    <ConfirmDialog
      open={open}
      title={getDialogMessage('destructive.delete.title', { entity: 'feature' })}
      description={getDialogMessage('destructive.delete.permanentDescription', {
        entity: 'feature',
      })}
      confirmLabel={getDialogMessage('destructive.delete.confirmLabel', { entity: 'feature' })}
      confirmColor="red"
      loading={loading}
      onClose={onClose}
      onConfirm={onConfirm}
    />
  );
};

type LinkSuiteToFeatureDialogProps = {
  open: boolean;
  saving?: boolean;
  value: string;
  suiteOptions: SuiteListItemDto[];
  errorMessage?: string | null;
  fieldError?: string;
  onClose: () => void;
  onSave: () => void;
  onSuiteChange: (value: string) => void;
};

export const LinkSuiteToFeatureDialog = ({
  open,
  saving = false,
  value,
  suiteOptions,
  errorMessage,
  fieldError,
  onClose,
  onSave,
  onSuiteChange,
}: LinkSuiteToFeatureDialogProps): ReactElement => {
  return (
    <LinkSelectDialog
      open={open}
      title={getDialogMessage('linking.link.title', { entity: 'suite', owner: 'feature' })}
      saving={saving}
      label="Suite"
      value={value}
      options={suiteOptions}
      fieldError={fieldError}
      errorMessage={errorMessage}
      description={getDialogMessage('linking.link.selectedDescription', {
        entity: 'suite',
        owner: 'feature',
      })}
      onClose={onClose}
      onSave={onSave}
      onChange={onSuiteChange}
    />
  );
};

type LinkTestToFeatureDialogProps = {
  open: boolean;
  saving?: boolean;
  value: string;
  testOptions: TestListItemDto[];
  errorMessage?: string | null;
  fieldError?: string;
  onClose: () => void;
  onSave: () => void;
  onTestChange: (value: string) => void;
};

export const LinkTestToFeatureDialog = ({
  open,
  saving = false,
  value,
  testOptions,
  errorMessage,
  fieldError,
  onClose,
  onSave,
  onTestChange,
}: LinkTestToFeatureDialogProps): ReactElement => {
  return (
    <LinkSelectDialog
      open={open}
      title={getDialogMessage('linking.link.title', { entity: 'test', owner: 'feature' })}
      saving={saving}
      label="Test"
      value={value}
      options={testOptions}
      fieldError={fieldError}
      errorMessage={errorMessage}
      description={getDialogMessage('linking.link.selectedDescription', {
        entity: 'test',
        owner: 'feature',
      })}
      onClose={onClose}
      onSave={onSave}
      onChange={onTestChange}
    />
  );
};

type UnlinkSuitesFromFeatureDialogProps = {
  open: boolean;
  saving?: boolean;
  suiteOptions: SuiteListItemDto[];
  selectedSuiteIds: string[];
  errorMessage?: string | null;
  disableSave?: boolean;
  onClose: () => void;
  onSave: () => void;
  onToggleSuite: (suiteId: string) => void;
};

export const UnlinkSuitesFromFeatureDialog = ({
  open,
  saving = false,
  suiteOptions,
  selectedSuiteIds,
  errorMessage,
  disableSave = false,
  onClose,
  onSave,
  onToggleSuite,
}: UnlinkSuitesFromFeatureDialogProps): ReactElement => {
  return (
    <UnlinkItemsDialog
      open={open}
      title={getDialogMessage('linking.unlink.title', { entity: 'suite', owner: 'feature' })}
      saving={saving}
      items={suiteOptions}
      selectedIds={selectedSuiteIds}
      itemLabel="Suites"
      errorMessage={errorMessage}
      disableSave={disableSave}
      description={getDialogMessage('linking.unlink.selectedDescription', {
        entity: 'suite',
        owner: 'feature',
      })}
      onClose={onClose}
      onSave={onSave}
      onToggle={onToggleSuite}
    />
  );
};

type UnlinkTestsFromFeatureDialogProps = {
  open: boolean;
  saving?: boolean;
  testOptions: TestListItemDto[];
  selectedTestIds: string[];
  errorMessage?: string | null;
  disableSave?: boolean;
  onClose: () => void;
  onSave: () => void;
  onToggleTest: (testId: string) => void;
};

export const UnlinkTestsFromFeatureDialog = ({
  open,
  saving = false,
  testOptions,
  selectedTestIds,
  errorMessage,
  disableSave = false,
  onClose,
  onSave,
  onToggleTest,
}: UnlinkTestsFromFeatureDialogProps): ReactElement => {
  return (
    <UnlinkItemsDialog
      open={open}
      title={getDialogMessage('linking.unlink.title', { entity: 'test', owner: 'feature' })}
      saving={saving}
      items={testOptions}
      selectedIds={selectedTestIds}
      itemLabel="Tests"
      errorMessage={errorMessage}
      disableSave={disableSave}
      description={getDialogMessage('linking.unlink.selectedDescription', {
        entity: 'test',
        owner: 'feature',
      })}
      onClose={onClose}
      onSave={onSave}
      onToggle={onToggleTest}
    />
  );
};
